import { useState, useEffect, useCallback } from 'react';
import {
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getNodeAlertConfigs,
  createNodeAlertConfig,
  updateNodeAlertConfig,
  toggleNodeAlertConfig,
  duplicateNodeAlertConfig,
  deleteNodeAlertConfig,
  getNodeCameras,
  getNodeGroups,
} from '../services/api.service';
import {
  EVENT_TYPE_OPTIONS,
  SEVERITY_OPTIONS,
  scopeOf,
  eventTypeLabel,
  parseEventTypes,
  type RuleScope,
} from '../constants/alertRules';

const blankConfig = () => ({
  name: '', event_type: '*', severity: '*', camera_id: null, group_id: null,
  min_confidence: 0.5, cooldown_seconds: 30, description: '',
  email_enabled: false, email_recipients: '', sms_enabled: false, sms_recipients: '',
  webhook_enabled: false, webhook_url: '', websocket_enabled: true, is_active: true,
});

function normalize(form: any) {
  const scope = scopeOf(form);
  return {
    ...form,
    // Empty selection means "all event types" — persist the wildcard so an empty rule
    // isn't silently dead (an empty event_type matches nothing at evaluation time).
    event_type: parseEventTypes(form.event_type).join(',') || '*',
    camera_id: scope === 'camera' ? form.camera_id : null,
    group_id: scope === 'group' ? form.group_id : null,
    min_confidence: Math.min(1, Math.max(0, parseFloat(form.min_confidence) || 0)),
    cooldown_seconds: Math.max(0, parseInt(form.cooldown_seconds, 10) || 0),
  };
}

export default function AnalyticsConfigPage() {
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  // Creating / editing / toggling / duplicating / deleting alert rules is gated
  // on alerts.config; the backend enforces it too. View stays open to any viewer.
  const canConfig = hasPermission('alerts.config');
  const nodeId = selectedNode?.nodeId || '';

  const [configs, setConfigs] = useState<any[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchConfigs = useCallback(async () => {
    if (!nodeId) { setConfigs([]); setLoading(false); return; }
    setLoading(true);
    try {
      setConfigs(await getNodeAlertConfigs(nodeId) || []);
    } catch (err) {
      console.error('Failed to fetch configs:', err);
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId).then((c) => setCameras(Array.isArray(c) ? c : c?.data || [])).catch(() => setCameras([]));
    getNodeGroups(nodeId).then((g) => setGroups(Array.isArray(g) ? g : g?.data || [])).catch(() => setGroups([]));
  }, [nodeId]);

  const handleToggle = async (id: string) => {
    try {
      const updated = await toggleNodeAlertConfig(nodeId, id);
      setConfigs((prev) => prev.map((c) => c.id === id ? { ...c, is_active: updated.is_active } : c));
    } catch (err) { console.error('Toggle failed:', err); }
  };
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this alert rule?')) return;
    try { await deleteNodeAlertConfig(nodeId, id); setConfigs((prev) => prev.filter((c) => c.id !== id)); }
    catch (err) { console.error('Delete failed:', err); }
  };
  const handleDuplicate = async (id: string) => {
    try { const clone = await duplicateNodeAlertConfig(nodeId, id); setConfigs((prev) => [clone, ...prev]); }
    catch (err) { console.error('Duplicate failed:', err); }
  };
  const handleCreate = async (data: any) => {
    try { const created = await createNodeAlertConfig(nodeId, normalize(data)); setConfigs((prev) => [created, ...prev]); setShowCreate(false); }
    catch (err) { console.error('Create failed:', err); }
  };
  const handleUpdate = async (id: string, data: any) => {
    try { const updated = await updateNodeAlertConfig(nodeId, id, normalize(data)); setConfigs((prev) => prev.map((c) => c.id === id ? updated : c)); setEditingId(null); }
    catch (err) { console.error('Update failed:', err); }
  };

  if (!nodeId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-6">
        Select a site to configure its alert rules.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Cog6ToothIcon className="w-6 h-6 text-brand" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">Alert Rules</h1>
            <p className="text-xs text-muted-foreground">Decide which AI detections on <span className="font-medium">{selectedNode?.name || nodeId}</span> become alerts.</p>
          </div>
          <span className="text-sm text-muted-foreground">({configs.length} rules)</span>
        </div>
        <div className="flex items-center gap-2">
          {canConfig && <button onClick={() => setShowCreate(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand text-white text-sm hover:opacity-90">
            <PlusIcon className="w-4 h-4" /> New Rule
          </button>}
          <button onClick={fetchConfigs} className="p-2 rounded-lg hover:bg-muted text-muted-foreground">
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)}</div>
        ) : configs.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p>No alert rules yet</p>
            {canConfig && <button onClick={() => setShowCreate(true)} className="mt-2 text-sm text-brand">Create your first rule</button>}
          </div>
        ) : (
          <div className="space-y-3">
            {configs.map((cfg) => (
              <ConfigCard
                key={cfg.id} config={cfg} cameras={cameras} groups={groups} canConfig={canConfig}
                isEditing={editingId === cfg.id}
                onEdit={() => setEditingId(cfg.id)} onCancelEdit={() => setEditingId(null)}
                onSave={(data: any) => handleUpdate(cfg.id, data)}
                onToggle={() => handleToggle(cfg.id)} onDelete={() => handleDelete(cfg.id)} onDuplicate={() => handleDuplicate(cfg.id)}
              />
            ))}
          </div>
        )}
      </div>

      {showCreate && <ConfigFormModal cameras={cameras} groups={groups} onSave={handleCreate} onClose={() => setShowCreate(false)} />}
    </div>
  );
}

const cameraName = (cams: any[], id: string) => cams.find((c) => c.id === id)?.name || id;
const groupName = (grps: any[], id: string) => grps.find((g) => g.id === id)?.name || id;
function scopeLabel(config: any, cameras: any[], groups: any[]) {
  const scope = scopeOf(config);
  if (scope === 'camera') return `Camera: ${cameraName(cameras, config.camera_id)}`;
  if (scope === 'group') return `Group: ${groupName(groups, config.group_id)}`;
  return 'All cameras';
}

function ConfigCard({ config, cameras, groups, canConfig, isEditing, onEdit, onCancelEdit, onSave, onToggle, onDelete, onDuplicate }: any) {
  const [form, setForm] = useState(config);
  useEffect(() => { setForm(config); }, [config]);

  if (isEditing) {
    return (
      <div className="rounded-lg border border-brand p-4 bg-card">
        <ConfigForm form={form} setForm={setForm} cameras={cameras} groups={groups} />
        <div className="flex gap-2 mt-3">
          <button onClick={() => onSave(form)} className="px-3 py-1.5 rounded bg-brand text-white text-sm">Save</button>
          <button onClick={onCancelEdit} className="px-3 py-1.5 rounded border border-border text-sm text-muted-foreground">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-border p-4 transition-colors ${config.is_active ? 'bg-card' : 'bg-muted opacity-60'}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-foreground">{config.name || `Rule: ${eventTypeLabel(config.event_type)}`}</h3>
            {!config.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Disabled</span>}
          </div>
          {config.description && <p className="text-xs text-muted-foreground mt-1">{config.description}</p>}
          <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground items-center">
            <span className="px-1.5 py-0.5 rounded bg-muted text-foreground">{eventTypeLabel(config.event_type)}</span>
            <span>{scopeLabel(config, cameras, groups)}</span>
            <span>•</span>
            <span>Severity: {config.severity === '*' ? 'auto' : config.severity}</span>
            <span>•</span>
            <span>Confidence ≥ {((config.min_confidence || 0) * 100).toFixed(0)}%</span>
            <span>•</span>
            <span>Cooldown {config.cooldown_seconds}s</span>
            {config.email_enabled && <span className="text-blue-400">📧 Email</span>}
            {config.sms_enabled && <span className="text-teal-400">📱 SMS</span>}
            {config.webhook_enabled && <span className="text-purple-400">🔗 Webhook</span>}
            {config.websocket_enabled && <span className="text-green-400">⚡ Live</span>}
          </div>
        </div>
        {canConfig ? (
        <div className="flex items-center gap-1">
          <button onClick={onToggle} className={`px-2 py-1 rounded text-xs ${config.is_active ? 'bg-green-500/15 text-green-500' : 'bg-muted text-muted-foreground'}`}>
            {config.is_active ? 'Active' : 'Inactive'}
          </button>
          <button onClick={onEdit} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Edit"><Cog6ToothIcon className="w-4 h-4" /></button>
          <button onClick={onDuplicate} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Duplicate"><DocumentDuplicateIcon className="w-4 h-4" /></button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-500/10 text-red-400" title="Delete"><TrashIcon className="w-4 h-4" /></button>
        </div>
        ) : (
          <span className={`px-2 py-1 rounded text-xs ${config.is_active ? 'bg-green-500/15 text-green-500' : 'bg-muted text-muted-foreground'}`}>
            {config.is_active ? 'Active' : 'Inactive'}
          </span>
        )}
      </div>
    </div>
  );
}

const inputCls = 'mt-0.5 w-full px-2 py-1 text-sm rounded border border-border bg-background text-foreground';
const labelCls = 'text-xs text-muted-foreground';

/**
 * Multi-select for event types. Stores a comma-separated string in `event_type`
 * (e.g. "crossing,loitering,person"), matched as OR at alert-evaluation time.
 * Pick from the known list, or type a custom type the AI pipeline emits.
 */
function EventTypeMultiSelect({ value, onChange }: { value?: string | null; onChange: (v: string) => void }) {
  const [custom, setCustom] = useState('');
  const selected = parseEventTypes(value);

  const commit = (next: string[]) => {
    const uniq = [...new Set(next.map((t) => String(t).trim()).filter(Boolean))];
    // '*' (all) is exclusive: it can't coexist with specific types.
    const cleaned = uniq.includes('*') && uniq.length > 1 ? uniq.filter((t) => t !== '*') : uniq;
    onChange(cleaned.join(','));
  };
  const add = (v: string) => {
    const t = String(v || '').trim();
    if (!t) return;
    if (t === '*') return commit(['*']);
    commit([...selected.filter((x) => x !== '*'), t]);
  };
  const remove = (v: string) => commit(selected.filter((t) => t !== v));

  const available = EVENT_TYPE_OPTIONS.filter((o) => !selected.includes(o.value));

  return (
    <div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1 rounded border border-border bg-background px-2 py-1 min-h-[34px]">
        {selected.length === 0 && <span className="text-sm text-muted-foreground">* (all event types)</span>}
        {selected.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded bg-brand/15 text-brand text-xs px-1.5 py-0.5">
            {eventTypeLabel(t)}
            <button type="button" onClick={() => remove(t)} className="leading-none hover:text-red-400" title="Remove">×</button>
          </span>
        ))}
      </div>
      <div className="mt-1 flex gap-1">
        <select value="" onChange={(e) => add(e.target.value)} className={inputCls}>
          <option value="" disabled>Add event type…</option>
          {available.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(custom); setCustom(''); } }}
          className={inputCls}
          placeholder="or type custom…"
        />
        <button type="button" onClick={() => { add(custom); setCustom(''); }} className="shrink-0 px-3 rounded border border-border text-sm text-muted-foreground hover:bg-muted">Add</button>
      </div>
    </div>
  );
}

function ConfigForm({ form, setForm, cameras, groups }: any) {
  const set = (key: string, val: any) => setForm({ ...form, [key]: val });
  const scope: RuleScope = scopeOf(form);

  const setScope = (next: RuleScope) => {
    if (next === 'all') setForm({ ...form, camera_id: null, group_id: null });
    else if (next === 'camera') setForm({ ...form, group_id: null, camera_id: form.camera_id || cameras[0]?.id || '' });
    else setForm({ ...form, camera_id: null, group_id: form.group_id || groups[0]?.id || '' });
  };

  const confidencePct = Math.round((parseFloat(form.min_confidence) || 0) * 100);

  return (
    <div className="space-y-4">
      <section>
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Detection rule</h4>
        <p className="text-[11px] text-muted-foreground mb-2">Matching detections become alerts. The most specific rule wins (camera &gt; group &gt; all).</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={labelCls}>Rule name</span>
            <input value={form.name || ''} onChange={(e) => set('name', e.target.value)} className={inputCls} placeholder="e.g. Loading dock intrusion" />
          </label>
          <div className="block col-span-2">
            <span className={labelCls}>Event types</span>
            <EventTypeMultiSelect value={form.event_type} onChange={(v: string) => set('event_type', v)} />
            <p className="text-[11px] text-muted-foreground mt-1">Add one or more types — the rule fires if a detection matches any of them. Leave empty for all types.</p>
          </div>
          <label className="block">
            <span className={labelCls}>Applies to</span>
            <select value={scope} onChange={(e) => setScope(e.target.value as RuleScope)} className={inputCls}>
              <option value="all">All cameras</option>
              <option value="camera">Specific camera</option>
              <option value="group">Specific group</option>
            </select>
          </label>
          {scope === 'camera' && (
            <label className="block">
              <span className={labelCls}>Camera</span>
              <select value={form.camera_id || ''} onChange={(e) => set('camera_id', e.target.value)} className={inputCls}>
                <option value="" disabled>Select camera…</option>
                {cameras.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          {scope === 'group' && (
            <label className="block">
              <span className={labelCls}>Group</span>
              <select value={form.group_id || ''} onChange={(e) => set('group_id', e.target.value)} className={inputCls}>
                <option value="" disabled>Select group…</option>
                {groups.map((g: any) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </label>
          )}
          <label className="block">
            <span className={labelCls}>Severity</span>
            <select value={form.severity || '*'} onChange={(e) => set('severity', e.target.value)} className={inputCls}>
              {SEVERITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Min confidence: {confidencePct}%</span>
            <input type="range" min={0} max={100} step={5} value={confidencePct}
              onChange={(e) => set('min_confidence', (parseInt(e.target.value, 10) || 0) / 100)} className="mt-2 w-full accent-brand" />
          </label>
          <label className="block">
            <span className={labelCls}>Cooldown (seconds)</span>
            <input type="number" min={0} value={form.cooldown_seconds ?? 0} onChange={(e) => set('cooldown_seconds', e.target.value)} className={inputCls} />
          </label>
          <label className="block col-span-2">
            <span className={labelCls}>Description (shown as the alert text)</span>
            <input value={form.description || ''} onChange={(e) => set('description', e.target.value)} className={inputCls} placeholder="Optional — defaults to an auto-generated summary" />
          </label>
        </div>
      </section>

      <section>
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Notifications</h4>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={form.websocket_enabled !== false} onChange={(e) => set('websocket_enabled', e.target.checked)} /> Live (in-app)
          </label>
          <div />
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={!!form.email_enabled} onChange={(e) => set('email_enabled', e.target.checked)} /> Email
          </label>
          <input value={form.email_recipients || ''} onChange={(e) => set('email_recipients', e.target.value)} disabled={!form.email_enabled} className={`${inputCls} disabled:opacity-50`} placeholder="a@x.com, b@y.com" />
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={!!form.sms_enabled} onChange={(e) => set('sms_enabled', e.target.checked)} /> SMS
          </label>
          <input value={form.sms_recipients || ''} onChange={(e) => set('sms_recipients', e.target.value)} disabled={!form.sms_enabled} className={`${inputCls} disabled:opacity-50`} placeholder="+1..., +1..." />
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={!!form.webhook_enabled} onChange={(e) => set('webhook_enabled', e.target.checked)} /> Webhook
          </label>
          <input value={form.webhook_url || ''} onChange={(e) => set('webhook_url', e.target.value)} disabled={!form.webhook_enabled} className={`${inputCls} disabled:opacity-50`} placeholder="https://..." />
        </div>
      </section>
    </div>
  );
}

function ConfigFormModal({ cameras, groups, onSave, onClose }: any) {
  const [form, setForm] = useState(blankConfig);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-card rounded-xl p-6 w-[560px] max-h-[85vh] overflow-y-auto shadow-2xl border border-border" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-4">Create Alert Rule</h2>
        <ConfigForm form={form} setForm={setForm} cameras={cameras} groups={groups} />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded border border-border text-sm text-muted-foreground">Cancel</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 rounded bg-brand text-white text-sm">Create</button>
        </div>
      </div>
    </div>
  );
}
